import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createApi } from "util/ApiCreator";
import { ProcessExecutionResponse, StepExecutionResponse } from "api/main/data-contracts";
import { ProcessExecutionAdminController } from "api/main/ProcessExecutionAdminController";
import { adminTheme, statusColors } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
  Grid,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DrawIcon from "@mui/icons-material/Draw";
import PersonIcon from "@mui/icons-material/Person";
import CheckIcon from "@mui/icons-material/Check";
import InputIcon from "@mui/icons-material/Input";
import DescriptionIcon from "@mui/icons-material/Description";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

function getStepTypeIcon(type?: string) {
  switch (type) {
    case "IDENTIFICATION":
      return <PersonIcon />;
    case "DATA_INPUT":
      return <InputIcon />;
    case "SIGN":
      return <DrawIcon />;
    case "CONFIRMATION":
      return <CheckIcon />;
    case "GENERATE_DOCUMENT":
      return <DescriptionIcon />;
    default:
      return <AssignmentIcon />;
  }
}

function getStepTypeName(type?: string) {
  switch (type) {
    case "IDENTIFICATION":
      return "Identifikacija";
    case "DATA_INPUT":
      return "Duomenų įvedimas";
    case "SIGN":
      return "Pasirašymas";
    case "CONFIRMATION":
      return "Patvirtinimas";
    case "GENERATE_DOCUMENT":
      return "Dokumento generavimas";
    default:
      return type || "Nežinomas";
  }
}

export default function AdminActionsPage() {
  const navigate = useNavigate();
  const executionApi = useRef(createApi("processexecutionadmin") as ProcessExecutionAdminController);

  const [executions, setExecutions] = useState<ProcessExecutionResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    getData();
  }, []);

  async function getData() {
    setLoading(true);
    try {
      const data = await executionApi.current.findAllForAction();
      // Sort by ID descending (newest first)
      const sorted = (data || []).sort((a: ProcessExecutionResponse, b: ProcessExecutionResponse) => (b.id || 0) - (a.id || 0));
      setExecutions(sorted);
    } catch (error) {
      console.error("Error fetching admin actions:", error);
    } finally {
      setLoading(false);
    }
  }

  function getActiveAdminStep(execution: ProcessExecutionResponse): StepExecutionResponse | undefined {
    return execution.stepExecutions?.find(
      (step) => step.status === "ACTIVE" && step.step?.executors?.includes("ADMIN")
    );
  }

  function getActiveStepIndex(execution: ProcessExecutionResponse): number {
    const index = execution.stepExecutions?.findIndex(
      (step) => step.status === "ACTIVE" && step.step?.executors?.includes("ADMIN")
    );
    return index !== undefined && index >= 0 ? index : 0;
  }

  function handleExecuteStep(execution: ProcessExecutionResponse) {
    const stepIndex = getActiveStepIndex(execution);
    navigate(`/process/execution/${execution.id}/${stepIndex}`);
  }

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: adminTheme.borderRadius.md,
                  background: adminTheme.gradients.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <NotificationsActiveIcon sx={{ color: "white", fontSize: 24 }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: adminTheme.colors.textPrimary,
                }}
              >
                Mano veiksmai
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textSecondary, ml: 8 }}
            >
              Procesai, kuriuose reikia jūsų veiksmų
            </Typography>
          </Box>
          <Tooltip title="Atnaujinti">
            <IconButton
              onClick={getData}
              sx={{
                bgcolor: adminTheme.colors.background,
                border: `1px solid ${adminTheme.colors.border}`,
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats Card */}
        <Card
          sx={{
            p: 3,
            mb: 4,
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            background: `linear-gradient(135deg, ${adminTheme.colors.primary}08 0%, ${adminTheme.colors.background} 100%)`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: executions.length > 0 ? statusColors.in_progress.bg : statusColors.finished.bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: executions.length > 0 ? statusColors.in_progress.text : statusColors.finished.text,
                }}
              >
                {executions.length}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                {executions.length === 0
                  ? "Nėra laukiančių veiksmų"
                  : executions.length === 1
                  ? "1 procesas laukia jūsų veiksmų"
                  : `${executions.length} procesai laukia jūsų veiksmų`}
              </Typography>
              <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                {executions.length > 0
                  ? "Pasirinkite procesą ir atlikite reikiamus veiksmus"
                  : "Visi veiksmai atlikti"}
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Action Cards */}
        {executions.length === 0 && !loading ? (
          <Card
            sx={{
              p: 6,
              textAlign: "center",
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <CheckIcon sx={{ fontSize: 64, color: adminTheme.colors.success, mb: 2 }} />
            <Typography variant="h6" sx={{ color: adminTheme.colors.textPrimary, mb: 1 }}>
              Puiku! Nėra laukiančių veiksmų
            </Typography>
            <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
              Kai bus procesų, kuriuose reikės jūsų veiksmų, jie bus rodomi čia
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {executions.map((execution) => {
              const activeStep = getActiveAdminStep(execution);
              return (
                <Grid item xs={12} md={6} lg={4} key={execution.id}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: adminTheme.borderRadius.lg,
                      border: `1px solid ${adminTheme.colors.border}`,
                      boxShadow: "none",
                      transition: adminTheme.transitions.normal,
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: adminTheme.shadows.cardHover,
                        borderColor: adminTheme.colors.primary,
                      },
                    }}
                    onClick={() => handleExecuteStep(execution)}
                  >
                    {/* Header */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: adminTheme.borderRadius.sm,
                            bgcolor: `${adminTheme.colors.primary}15`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: adminTheme.colors.primary,
                          }}
                        >
                          {getStepTypeIcon(activeStep?.type)}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
                            Proceso ID: #{execution.id}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                            {execution.process?.name || "Procesas"}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        label="Laukia"
                        size="small"
                        sx={{
                          bgcolor: statusColors.in_progress.bg,
                          color: statusColors.in_progress.text,
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    {/* Step Info */}
                    <Box
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: adminTheme.borderRadius.sm,
                        bgcolor: adminTheme.colors.backgroundAlt,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
                        Reikalingas veiksmas:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                        {activeStep?.name || getStepTypeName(activeStep?.type)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, mt: 0.5 }}>
                        Tipas: {getStepTypeName(activeStep?.type)}
                      </Typography>
                    </Box>

                    {/* User Info */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                      <PersonIcon sx={{ fontSize: 16, color: adminTheme.colors.textMuted }} />
                      <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
                        {execution.accountInfoResponse?.email || "Nežinomas vartotojas"}
                      </Typography>
                    </Box>

                    {/* Action Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PlayArrowIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExecuteStep(execution);
                      }}
                      sx={{
                        background: adminTheme.gradients.primary,
                        borderRadius: adminTheme.borderRadius.md,
                        py: 1.5,
                        fontWeight: 600,
                        "&:hover": {
                          background: adminTheme.gradients.primaryHover,
                        },
                      }}
                    >
                      Vykdyti žingsnį
                    </Button>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </Box>
  );
}
